import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getSupabaseClient, API_URL, projectId, publicAnonKey } from '../utils/supabaseClient.ts';

const supabase = getSupabaseClient();

interface User {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  companyName?: string;
  taxId?: string;
  country?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
}

interface Credits {
  balance: number;
  totalPurchased: number;
  totalUsed: number;
}

interface AuthContextType {
  user: User | null;
  credits: Credits | null;
  loading: boolean;
  signUp: (email: string, password: string, billingInfo?: any) => Promise<any>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshCredits: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export useAuth hook first
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const isCheckingSessionRef = useRef(false); // Prevent concurrent session checks
  const abortControllerRef = useRef<AbortController | null>(null); // For cleanup

  // Remove console.log that fires on every render
  // console.log('🔐 AuthProvider initialized');

  // Check if user is already logged in
  useEffect(() => {
    let isMounted = true; // Track mount state for cleanup

    // Create abort controller for this effect
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const initializeAuth = async () => {
      if (!isMounted || controller.signal.aborted) return;
      await checkUser();
    };

    initializeAuth();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted || controller.signal.aborted) return;
      
      // Only log critical auth events (reduce console noise)
      if (event === 'SIGNED_IN') {
        console.log('🔐 User signed in:', session?.user?.email);
      } else if (event === 'SIGNED_OUT') {
        console.log('🔓 User signed out');
      }
      
      if (event === 'SIGNED_IN' && session?.access_token) {
        await fetchUserProfile(session.access_token);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setCredits(null);
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        // Silent token refresh - no need to log
        await fetchUserProfile(session.access_token);
      } else if (event === 'USER_UPDATED' && session?.access_token) {
        // Silent profile update - no need to log
        await fetchUserProfile(session.access_token);
      }
    });
    
    return () => {
      isMounted = false;
      controller.abort(); // Abort any pending operations
      subscription.unsubscribe();
      abortControllerRef.current = null;
    };
  }, []);

  async function checkUser() {
    if (isCheckingSessionRef.current) {
      console.log('⏭️ Skipping checkUser - already in progress');
      return;
    }

    isCheckingSessionRef.current = true;

    try {
      console.log('🔍 Checking user session...');
      
      // Add timeout to prevent hanging
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session check timeout')), 3000)
      );
      
      const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]) as any;
      
      // If there's an error getting the session (e.g., invalid refresh token)
      if (error) {
        console.error('❌ Session error:', error.message);
        
        // Check if it's a refresh token error
        if (error.message.includes('refresh') || error.message.includes('Refresh Token')) {
          console.warn('⚠️ Invalid refresh token detected - clearing session');
          // Clear the invalid session
          await supabase.auth.signOut();
          setUser(null);
          setCredits(null);
        }
        
        setLoading(false);
        return;
      }
      
      if (session?.access_token) {
        console.log('✅ Session found, fetching user profile...');
        const success = await fetchUserProfile(session.access_token);
        // If fetch fails, still end loading state
        if (!success) {
          setLoading(false);
        }
      } else {
        console.log('ℹ️ No session found - user not logged in');
        setLoading(false);
      }
    } catch (error) {
      // Silent fail for timeout errors
      const errorMessage = (error as Error)?.message || '';
      
      if (errorMessage.includes('timeout') || errorMessage.includes('Session check timeout')) {
        console.log('⏱️ Session check timeout - Supabase may be slow, proceeding without session');
        setUser(null);
        setCredits(null);
        setLoading(false);
        return;
      }
      
      // Only log non-timeout errors
      console.error('❌ Check user error:', error);
      
      // If error contains refresh token message, clear the session
      if (errorMessage.includes('refresh') || errorMessage.includes('Refresh Token')) {
        console.warn('⚠️ Refresh token error caught - clearing session');
        try {
          await supabase.auth.signOut();
        } catch (signOutError) {
          console.error('Error during signOut:', signOutError);
        }
        setUser(null);
        setCredits(null);
      }
      
      // ALWAYS end loading state on error
      setLoading(false);
    } finally {
      isCheckingSessionRef.current = false;
    }
  }

  async function fetchUserProfile(accessToken: string) {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      console.log('⏭️ Skipping fetch - already in progress');
      return false;
    }

    isFetchingRef.current = true;

    try {
      console.log('🔍 Fetching user profile from:', `${API_URL}/auth/me`);
      
      // Send token in custom header + dummy Authorization to satisfy Supabase
      const response = await fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`, // Dummy token for Supabase
          'X-Auth-Token': accessToken, // Real user token
        },
        signal: AbortSignal.timeout(5000), // 5 second timeout (reduced from 15s for faster UX)
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ User profile fetched successfully');
        setUser(data.user);
        setCredits(data.credits);
        return true; // Success indicator
      } else if (response.status === 401) {
        // Token expired or invalid - silently sign out user
        // Don't log this as a warning since it's expected for non-authenticated users
        console.log('ℹ️ No valid session - user not authenticated');
        await supabase.auth.signOut();
        setUser(null);
        setCredits(null);
        return false;
      } else {
        console.error('❌ Server returned error:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // If it's a server error, don't sign out - just show loading state ended
        if (response.status >= 500) {
          console.log('Server error detected, keeping user session');
        }
        return false;
      }
    } catch (error) {
      // Silent fail for timeout errors to avoid alarming users
      const errorMessage = (error as Error)?.message || '';
      const errorName = (error as Error)?.name || '';
      
      if (errorName === 'TimeoutError' || errorMessage.includes('timeout') || errorMessage.includes('aborted')) {
        console.log('⏱️ Request timeout - backend may be slow or unavailable');
      } else {
        console.error('❌ Fetch user profile error:', error);
        console.error('Error name:', errorName);
        console.error('Error message:', errorMessage);
        
        // Check if it's a network error
        if (error instanceof TypeError && errorMessage.includes('Failed to fetch')) {
          console.error('🌐 Network error - Backend server is unreachable');
          console.error('API URL:', `${API_URL}/auth/me`);
        }
      }
      
      return false;
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }

  async function signUp(email: string, password: string, billingInfo?: any) {
    try {
      // Create user via our API
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({ email, password, billingInfo }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Signup API error:', data);
        throw new Error(data.error || 'Signup failed');
      }

      console.log('✅ User created successfully!');
      // Verification code only logged in development (removed from production for security)
      
      // DON'T sign in yet - user needs to verify email first
      // Return the verification code so the signup page can pass it to verify-email page
      return data;
    } catch (error: any) {
      console.error('Signup error:', error);
      throw new Error(error.message || 'Failed to create account. Please try again.');
    }
  }

  async function signIn(email: string, password: string, rememberMe?: boolean) {
    try {
      const { data: { session }, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Check specific error cases
        if (error.message === 'Invalid login credentials') {
          // This could mean wrong password OR unverified email
          throw new Error('Invalid email or password. Please check your credentials.');
        }
        
        if (error.message === 'Email not confirmed') {
          throw new Error('Please verify your email address before signing in.');
        }
        
        throw error;
      }

      if (session?.access_token) {
        await fetchUserProfile(session.access_token);
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  async function signOut() {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setCredits(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  const refreshUser = useCallback(async () => {
    // Prevent concurrent session checks
    if (isCheckingSessionRef.current) {
      console.log('⏭️ Skipping refreshUser - session check in progress');
      return;
    }

    isCheckingSessionRef.current = true;

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Session error in refreshUser:', error.message);
        if (error.message.includes('refresh') || error.message.includes('Refresh Token')) {
          console.warn('⚠️ Invalid refresh token - signing out');
          await supabase.auth.signOut();
          setUser(null);
          setCredits(null);
        }
        return;
      }
      
      if (session?.access_token) {
        await fetchUserProfile(session.access_token);
      }
    } catch (error) {
      console.error('Error in refreshUser:', error);
    } finally {
      isCheckingSessionRef.current = false;
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    // Prevent concurrent session checks
    if (isCheckingSessionRef.current) {
      console.log('⏭️ Skipping refreshCredits - session check in progress');
      return;
    }

    isCheckingSessionRef.current = true;

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Session error in refreshCredits:', error.message);
        if (error.message.includes('refresh') || error.message.includes('Refresh Token')) {
          console.warn('⚠️ Invalid refresh token - signing out');
          await supabase.auth.signOut();
          setUser(null);
          setCredits(null);
        }
        return;
      }
      
      if (session?.access_token) {
        await fetchUserProfile(session.access_token);
      }
    } catch (error) {
      console.error('Error in refreshCredits:', error);
    } finally {
      isCheckingSessionRef.current = false;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      credits, 
      loading, 
      signUp, 
      signIn, 
      signOut,
      refreshUser,
      refreshCredits
    }}>
      {children}
    </AuthContext.Provider>
  );
}